import {
  Add,
  ArrowLeft2,
  Camera,
  Document,
  Microphone2,
  Sticker,
} from 'iconsax-react';

import { cn } from '@src/design-system/helpers';
import { messageContentToPreviewText } from '@shared/features/reminders/helpers/use-template-channel-content-drafts';
import {
  OrganizationPreviewAvatar,
  useCurrentOrganizationPreview,
} from '@src/shared/components/organization-preview-avatar';
import type { TMedia } from '@src/shared/types/media';

export type WhatsAppPreviewProps = {
  className?: string;
  content?: string;
  attachments?: TMedia[];
};

function formatSize(bytes: number) {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

function isImage(m: TMedia) {
  return m.mime_type?.startsWith('image/') ?? false;
}

const WA_BLUE = '#007AFF';

function SentBubble({
  children,
  time,
}: {
  children: React.ReactNode;
  time: string;
}) {
  return (
    <div className='ml-auto max-w-[92%] rounded-lg rounded-tr-sm bg-[#DCF7C5] px-2 pb-1 pt-1.5 shadow-sm'>
      {children}
      <div className='mt-1 flex items-center justify-end gap-1 pr-0.5'>
        <span className='text-[10px] text-gray-400'>{time}</span>
        <span
          className='pb-0.5 text-[10px] leading-[-4px] text-[#53bdeb]'
          aria-hidden
        >
          <span className='-mr-1'>✓</span>
          <span>✓</span>
        </span>
      </div>
    </div>
  );
}

/**
 * WhatsApp mobile chat (300×650 inner screen): header, “Today”, bubbles, white composer with blue actions.
 */
export function WhatsAppPreview({
  className,
  content,
  attachments,
}: WhatsAppPreviewProps) {
  const { name: organizationName } = useCurrentOrganizationPreview();
  const text = messageContentToPreviewText(content);
  const hasText = Boolean(text);
  const hasFiles = Boolean(attachments?.length);

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col bg-[#e5ddd5] font-sans text-gray-900 [-webkit-font-smoothing:antialiased]',
        className
      )}
      style={{ fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif' }}
    >
      <header className='flex shrink-0 items-center bg-[#075e54] px-1.5 py-2 pt-6 text-white'>
        <button
          type='button'
          className='flex size-9 items-center justify-center rounded-full hover:bg-white/10'
          aria-label='Back'
        >
          <ArrowLeft2
            size={22}
            variant='Outline'
            color='currentColor'
          />
        </button>
        <div className='flex items-center gap-2'>
          <OrganizationPreviewAvatar
            className='size-7'
            fallbackClassName='bg-[#cfd4d6] text-[13px] font-semibold text-[#54656f]'
          />
          <div className='min-w-0 flex-1 leading-tight'>
            <p className='truncate text-sm font-medium'>
              {organizationName || 'Organization'}
            </p>
            <p className='truncate text-xs text-white/85'>Organization</p>
          </div>
        </div>
      </header>

      <div className='relative flex min-h-0 flex-1 flex-col overflow-hidden'>
        <div
          className='pointer-events-none absolute inset-0 opacity-[0.085]'
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='%23000' fill-opacity='.25' d='M40 8c1 0 2 1 2 2v6c0 1-1 2-2 2s-2-1-2-2v-6c0-1 1-2 2-2zm0 56c1 0 2 1 2 2v2c0 1-1 2-2 2s-2-1-2-2v-2c0-1 1-2 2-2zM8 40c0-1 1-2 2-2h6c1 0 2 1 2 2s-1 2-2 2h-6c-1 0-2-1-2-2zm56 0c0-1 1-2 2-2h2c1 0 2 1 2 2s-1 2-2 2h-2c-1 0-2-1-2-2z'/%3E%3C/svg%3E")`,
          }}
          aria-hidden
        />
        <div className='pointer-events-auto relative flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pb-2 pt-3 scrollbar-gray'>
          <div className='mb-3 flex justify-center'>
            <span className='rounded-sm bg-[#DDDDE9] px-2.5 py-0.5 text-[10px] font-semibold text-[#3C3C43] shadow-sm'>
              Today
            </span>
          </div>

          <div className='flex flex-col gap-1.5'>
            {!hasText && !hasFiles ? (
              <p className='py-6 text-center text-[13px] text-[#667781]'>
                No message
              </p>
            ) : null}
            {hasText ? (
              <SentBubble time='11:50'>
                <p className='whitespace-pre-wrap break-words pr-10 text-[14.5px] leading-[1.38] text-black'>
                  {text}
                </p>
              </SentBubble>
            ) : null}
            {hasFiles ? (
              <SentBubble time='11:51'>
                <div className='flex flex-col gap-1.5'>
                  {attachments!.map((m) =>
                    isImage(m) ? (
                      <a
                        key={m.id}
                        href={m.file}
                        target='_blank'
                        rel='noreferrer'
                        className='block max-h-32 overflow-hidden rounded-md'
                      >
                        <img
                          src={m.file}
                          alt={m.name}
                          className='max-h-32 w-full object-cover'
                        />
                      </a>
                    ) : (
                      <a
                        key={m.id}
                        href={m.file}
                        target='_blank'
                        rel='noreferrer'
                        className='flex items-stretch gap-2 overflow-hidden rounded-md bg-white'
                      >
                        <div className='flex w-14 shrink-0 items-center justify-center bg-[#e6f0ff]'>
                          <Document
                            size={32}
                            variant='Bold'
                            className='text-[#1a73e8]'
                          />
                        </div>
                        <div className='flex min-w-0 flex-1 flex-col justify-center py-1.5 pr-2'>
                          <span className='truncate text-[14px] font-medium text-black'>
                            {m.name || 'Description'}
                          </span>
                          <span className='text-[12px] text-[#667781]'>
                            {formatSize(m.size)} —{' '}
                            {(m.mime_type || 'file').split('/')[1] || 'file'}
                          </span>
                        </div>
                      </a>
                    )
                  )}
                </div>
              </SentBubble>
            ) : null}
          </div>
        </div>
      </div>

      <footer className='flex shrink-0 items-center gap-1.5 border-t border-[#e7e7e7] bg-white px-1.5 py-1.5'>
        <button
          type='button'
          className='flex size-6 shrink-0 items-center justify-center rounded-full font-light text-[26px] leading-none'
          style={{ color: WA_BLUE }}
          aria-label='Attach'
        >
          <Add />
        </button>
        <div className='flex min-h-[32px] flex-1 items-center rounded-full border border-[#e2e2e2] bg-white pr-1 text-[15px] text-[#8696a0]'>
          <button
            type='button'
            className='flex size-6 shrink-0 items-center justify-center ml-auto'
            style={{ color: WA_BLUE }}
            aria-label='Sticker'
          >
            <Sticker
              size={20}
              variant='Outline'
              color={WA_BLUE}
            />
          </button>
        </div>
        <button
          type='button'
          className='flex size-6 shrink-0 items-center justify-center'
          style={{ color: WA_BLUE }}
          aria-label='Camera'
        >
          <Camera
            size={20}
            variant='Outline'
            color={WA_BLUE}
          />
        </button>
        <button
          type='button'
          className='flex size-6 shrink-0 items-center justify-center'
          style={{ color: WA_BLUE }}
          aria-label='Voice'
        >
          <Microphone2
            size={20}
            variant='Outline'
            color={WA_BLUE}
          />
        </button>
      </footer>
    </div>
  );
}
